/**
 * Google Ads Adapter (Story 4.8, expanded IMC-9, IMC-23)
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls and delegates discovery to
 * the existing discoverGoogleAdsAccounts() function.
 *
 * ADR-013: Adapter Pattern for API Isolation
 * ADR-014: Defensive API Versioning (v23 via API_VERSIONS.googleAds)
 *
 * OAuth is NOT part of this adapter — handled by Story 4.5.
 * Token refresh is NOT implemented — deferred to Story 4.12+.
 * KV caching is NOT implemented — deferred to Story 4.11.
 */

import type {
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  DimensionDefinition,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";
import { discoverGoogleAdsAccounts } from "./discovery";
import { GOOGLE_INTEGRATION_SCOPES } from "./scopes";
import { API_VERSIONS } from "../config";

// ============================================================================
// Constants
// ============================================================================

const PLATFORM = "google-ads" as const;

const GOOGLE_ADS_API_BASE = "https://googleads.googleapis.com";

/** Per-call timeout for Google Ads queries (3s per query, 10 queries in parallel via Promise.all — ~3s wall clock) */
const QUERY_TIMEOUT_MS = 3_000;

/** Validation timeout (5s for lightweight GAQL query) */
const VALIDATE_TIMEOUT_MS = 5_000;

/** Max accounts to enrich in parallel during discoverAccounts (Worker limit safety) */
const MAX_PARALLEL_ENRICHMENTS = 5;

/**
 * Manager (MCC) subtree expansion query (Story 31-16 AC #1).
 *
 * `customer_client` returns ALL levels of the manager's subtree in ONE
 * searchStream query (level 0 = the manager itself, level >= 1 = descendants)
 * — no recursion into sub-managers and no pageToken loop needed.
 * ENABLED + non-hidden filtering keeps dead/hidden accounts out of the
 * catalog at the only supported layer (cancelled accounts have no listing API).
 */
const CUSTOMER_CLIENT_QUERY =
  "SELECT customer_client.id, customer_client.descriptive_name, customer_client.currency_code, customer_client.time_zone, customer_client.manager, customer_client.level, customer_client.status, customer_client.hidden FROM customer_client WHERE customer_client.status = 'ENABLED' AND customer_client.hidden = FALSE";

/**
 * Fresh `scopes` array for catalog rows (Story 31-16 AC #3).
 * Single source of truth: `GOOGLE_INTEGRATION_SCOPES["google-ads"].scopes`
 * (= `["https://www.googleapis.com/auth/adwords"]`). A fresh array is
 * returned per call — never a shared mutable reference into the catalog
 * upsert. Display/consent metadata ONLY, never access control (31-4).
 */
function googleAdsCatalogScopes(): string[] {
  return [...(GOOGLE_INTEGRATION_SCOPES[PLATFORM]?.scopes ?? [])];
}

/** Micros conversion: Google Ads cost values are in millionths of account currency */
const MICROS_PER_UNIT = 1_000_000;

/**
 * Map QualityScoreBucket enum to a 1-10 numeric score (AC #3, IMC-23).
 * Google returns BELOW_AVERAGE, AVERAGE, ABOVE_AVERAGE for QS sub-components.
 */
const QS_BUCKET_TO_SCORE: Record<string, number> = {
  BELOW_AVERAGE: 3,
  AVERAGE: 6,
  ABOVE_AVERAGE: 9,
};

// ============================================================================
// Report Index Constants
// ============================================================================

const REPORT_INDEX = {
  OVERVIEW: 0,
  CAMPAIGNS: 1,
  AD_GROUPS: 2,
  KEYWORDS: 3,
  SEARCH_TERMS: 4,
  SHOPPING: 5,
  AGE_RANGE: 6,
  GENDER: 7,
  ADS: 8,
  CONVERSION_DETAIL: 9,
} as const;

// ============================================================================
// Google Ads API Response Types (local to adapter, NOT exported)
// ============================================================================

interface GoogleAdsSearchStreamResponse {
  results?: Array<Record<string, unknown>>;
  fieldMask?: string;
  requestId?: string;
}

// ============================================================================
// Helpers
// ============================================================================

/** Convert micros (1/1,000,000) to currency */
const microsToCurrency = (micros: number): number => micros / MICROS_PER_UNIT;

/**
 * Safely parse a Google Ads metric value.
 * REST API returns many values as strings, so parse with Number().
 */
function parseMetric(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Parse a nullable metric (e.g. Quality Score 1-10).
 * Returns null when value is absent, preserving "no data" semantics.
 */
function parseNullableMetric(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Parse a QualityScoreBucket enum to a 1-10 numeric score (IMC-23, AC #3).
 * Returns null when value is absent or unrecognized (UNSPECIFIED/UNKNOWN).
 */
function parseQsBucket(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const s = String(v);
  return QS_BUCKET_TO_SCORE[s] ?? null;
}

/**
 * Build standard Google Ads headers for all API calls.
 */
function buildGoogleAdsHeaders(
  accessToken: string,
  developerToken: string,
  loginCustomerId?: string
): Record<string, string> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    "developer-token": developerToken,
    "Content-Type": "application/json",
  };
  if (loginCustomerId) {
    headers["login-customer-id"] = loginCustomerId;
  }
  return headers;
}

/**
 * Safely parse a Google Ads searchStream response.
 * Consumes the response body EXACTLY ONCE.
 */
async function parseGoogleAdsResponse(
  response: Response,
  queryName: string
): Promise<AdapterResult<GoogleAdsSearchStreamResponse[]>> {
  try {
    return adapterOk(
      (await response.json()) as GoogleAdsSearchStreamResponse[]
    );
  } catch (err) {
    return adapterErr({
      code: "invalid_response",
      message: sanitizeErrorMessage(
        `Failed to parse Google Ads ${queryName} response: ${err instanceof Error ? err.message : String(err)}`
      ),
      retryable: false,
    });
  }
}

/**
 * Flatten searchStream results from all batches.
 * SearchStream returns an array of batches, each with a results array.
 */
function flattenResults(
  data: GoogleAdsSearchStreamResponse[]
): Array<Record<string, unknown>> {
  return data.flatMap((batch) => batch.results ?? []);
}

/**
 * Execute a single GAQL query via searchStream.
 */
async function executeGaqlQuery(
  customerId: string,
  query: string,
  queryName: string,
  headers: Record<string, string>,
  timeoutMs: number
): Promise<AdapterResult<Array<Record<string, unknown>>>> {
  const url = `${GOOGLE_ADS_API_BASE}/${API_VERSIONS.googleAds}/customers/${customerId}/googleAds:searchStream`;

  const result = await safeFetch(
    url,
    {
      method: "POST",
      headers,
      body: JSON.stringify({ query }),
    },
    timeoutMs
  );

  if (!result.success) {
    return adapterErr(result.error);
  }

  const parseResult = await parseGoogleAdsResponse(result.data, queryName);
  if (!parseResult.success) {
    return adapterErr(parseResult.error);
  }

  return adapterOk(flattenResults(parseResult.data));
}

// ============================================================================
// GAQL Query Builders
// ============================================================================

function buildOverviewQuery(startDate: string, endDate: string): string {
  return `SELECT metrics.impressions, metrics.clicks, metrics.ctr, metrics.average_cpc, metrics.cost_micros, metrics.conversions, metrics.cost_per_conversion, metrics.conversions_value, metrics.conversions_value_per_cost, metrics.all_conversions, metrics.all_conversions_value, metrics.cost_per_all_conversions, metrics.value_per_conversion, metrics.view_through_conversions, metrics.cross_device_conversions, metrics.all_conversions_by_conversion_date, metrics.all_conversions_value_by_conversion_date, metrics.conversions_by_conversion_date, metrics.conversions_value_by_conversion_date, metrics.current_model_attributed_conversions, metrics.current_model_attributed_conversions_value, metrics.conversions_from_interactions_rate, metrics.all_conversions_value_per_cost, metrics.average_cpm, metrics.average_cpe, metrics.average_cost, metrics.interactions, metrics.interaction_rate, metrics.engagements, metrics.engagement_rate, metrics.phone_impressions, metrics.phone_calls, metrics.phone_through_rate, metrics.average_frequency, metrics.unique_users, metrics.active_view_impressions, metrics.active_view_viewability, metrics.active_view_measurability, metrics.active_view_measurable_impressions, metrics.video_trueview_views, metrics.video_trueview_view_rate, metrics.trueview_average_cpv, metrics.video_quartile_p25_rate, metrics.video_quartile_p50_rate, metrics.video_quartile_p75_rate, metrics.video_quartile_p100_rate, metrics.video_watch_time_duration_millis, metrics.average_video_watch_time_duration_millis FROM customer WHERE segments.date BETWEEN '${startDate}' AND '${endDate}'`;
}

function buildCampaignsQuery(startDate: string, endDate: string): string {
  return `SELECT campaign.id, campaign.name, campaign.status, campaign.advertising_channel_type, campaign.bidding_strategy_type, campaign.labels, metrics.impressions, metrics.clicks, metrics.ctr, metrics.average_cpc, metrics.cost_micros, metrics.conversions, metrics.cost_per_conversion, metrics.conversions_value, metrics.conversions_value_per_cost, metrics.all_conversions, metrics.all_conversions_value, metrics.view_through_conversions, metrics.cross_device_conversions, metrics.search_impression_share, metrics.search_budget_lost_impression_share, metrics.search_rank_lost_impression_share, metrics.search_absolute_top_impression_share, metrics.search_budget_lost_absolute_top_impression_share, metrics.search_rank_lost_absolute_top_impression_share, metrics.search_top_impression_share, metrics.search_budget_lost_top_impression_share, metrics.search_rank_lost_top_impression_share, metrics.absolute_top_impression_percentage, metrics.top_impression_percentage, metrics.display_impression_share, metrics.display_rank_lost_impression_share, metrics.display_budget_lost_impression_share, metrics.video_trueview_views, metrics.video_trueview_view_rate, metrics.trueview_average_cpv, metrics.video_quartile_p25_rate, metrics.video_quartile_p50_rate, metrics.video_quartile_p75_rate, metrics.video_quartile_p100_rate, metrics.video_watch_time_duration_millis, metrics.average_video_watch_time_duration_millis, metrics.average_cpm, metrics.average_cpe, metrics.average_cost, metrics.interactions, metrics.interaction_rate, metrics.engagements, metrics.engagement_rate, metrics.phone_impressions, metrics.phone_calls, metrics.phone_through_rate, metrics.average_frequency, metrics.unique_users, metrics.active_view_impressions, metrics.active_view_viewability, metrics.active_view_measurability, metrics.active_view_measurable_impressions FROM campaign WHERE segments.date BETWEEN '${startDate}' AND '${endDate}' AND campaign.status != 'REMOVED' ORDER BY metrics.cost_micros DESC LIMIT 20`;
}

function buildAdGroupsQuery(startDate: string, endDate: string): string {
  return `SELECT campaign.name, ad_group.id, ad_group.name, ad_group.status, metrics.impressions, metrics.clicks, metrics.ctr, metrics.cost_micros, metrics.conversions, metrics.cost_per_conversion FROM ad_group WHERE segments.date BETWEEN '${startDate}' AND '${endDate}' AND campaign.status != 'REMOVED' AND ad_group.status != 'REMOVED' ORDER BY metrics.cost_micros DESC LIMIT 20`;
}

function buildKeywordsQuery(startDate: string, endDate: string): string {
  return `SELECT campaign.name, ad_group.name, ad_group_criterion.keyword.text, ad_group_criterion.keyword.match_type, metrics.impressions, metrics.clicks, metrics.ctr, metrics.cost_micros, metrics.conversions, metrics.historical_quality_score, metrics.historical_creative_quality_score, metrics.historical_landing_page_quality_score, metrics.historical_search_predicted_ctr FROM keyword_view WHERE segments.date BETWEEN '${startDate}' AND '${endDate}' AND campaign.status != 'REMOVED' AND ad_group.status != 'REMOVED' AND ad_group_criterion.status != 'REMOVED' ORDER BY metrics.impressions DESC LIMIT 20`;
}

function buildSearchTermsQuery(startDate: string, endDate: string): string {
  return `SELECT search_term_view.search_term, segments.search_term_match_type, metrics.impressions, metrics.clicks, metrics.ctr, metrics.cost_micros, metrics.conversions FROM search_term_view WHERE segments.date BETWEEN '${startDate}' AND '${endDate}' ORDER BY metrics.impressions DESC LIMIT 50`;
}

function buildShoppingQuery(startDate: string, endDate: string): string {
  return `SELECT segments.product_item_id, segments.product_title, segments.product_brand, segments.product_category_level1, segments.product_type_l1, segments.product_channel, segments.product_custom_attribute0, metrics.impressions, metrics.clicks, metrics.cost_micros, metrics.conversions, metrics.revenue_micros, metrics.units_sold, metrics.gross_profit_micros, metrics.average_order_value_micros, metrics.cost_of_goods_sold_micros, metrics.cross_sell_revenue_micros, metrics.cross_sell_units_sold, metrics.lead_revenue_micros, metrics.lead_units_sold FROM shopping_performance_view WHERE segments.date BETWEEN '${startDate}' AND '${endDate}' ORDER BY metrics.revenue_micros DESC LIMIT 50`;
}

function buildAgeRangeQuery(startDate: string, endDate: string): string {
  return `SELECT ad_group_criterion.age_range.type, metrics.impressions, metrics.clicks, metrics.ctr, metrics.cost_micros, metrics.conversions FROM age_range_view WHERE segments.date BETWEEN '${startDate}' AND '${endDate}' ORDER BY metrics.impressions DESC LIMIT 20`;
}

function buildGenderQuery(startDate: string, endDate: string): string {
  return `SELECT ad_group_criterion.gender.type, metrics.impressions, metrics.clicks, metrics.ctr, metrics.cost_micros, metrics.conversions FROM gender_view WHERE segments.date BETWEEN '${startDate}' AND '${endDate}' ORDER BY metrics.impressions DESC LIMIT 20`;
}

// IMC-23: Ad-level query (ad ID, ad type, ad strength)
function buildAdsQuery(startDate: string, endDate: string): string {
  return `SELECT campaign.name, ad_group.name, ad_group_ad.ad.id, ad_group_ad.ad.type, ad_group_ad.ad_strength, metrics.impressions, metrics.clicks, metrics.ctr, metrics.cost_micros, metrics.conversions FROM ad_group_ad WHERE segments.date BETWEEN '${startDate}' AND '${endDate}' AND campaign.status != 'REMOVED' AND ad_group.status != 'REMOVED' AND ad_group_ad.status != 'REMOVED' ORDER BY metrics.impressions DESC LIMIT 20`;
}

// IMC-23: Conversion detail query (conversion action name, category, lag bucket)
function buildConversionDetailQuery(startDate: string, endDate: string): string {
  return `SELECT segments.conversion_action_name, segments.conversion_action_category, segments.conversion_lag_bucket, metrics.conversions, metrics.conversions_value, metrics.all_conversions FROM campaign WHERE segments.date BETWEEN '${startDate}' AND '${endDate}' AND campaign.status != 'REMOVED' ORDER BY metrics.conversions DESC LIMIT 50`;
}

// ============================================================================
// Response Transformation
// ============================================================================

/** Default zero-value overview record used when no data is returned */
const EMPTY_OVERVIEW: Record<string, unknown> = {
  _reportType: "overview",
  impressions: 0, clicks: 0, ctr: 0, averageCpc: 0, cost: 0,
  conversions: 0, conversionRate: 0, costPerConversion: 0,
  conversionsValue: 0, roas: 0, allRoas: 0, allConversions: 0, allConversionsValue: 0,
  viewThroughConversions: 0, crossDeviceConversions: 0, valuePerConversion: 0,
  costPerAllConversions: 0, conversionsByConvDate: 0, conversionsValueByConvDate: 0,
  allConversionsByConvDate: 0, allConversionsValueByConvDate: 0,
  modelAttributedConversions: 0, modelAttributedConversionsValue: 0,
  conversionsFromInteractionsRate: 0,
  // Delivery & engagement
  averageCpm: 0, averageCpe: 0, averageCost: 0,
  interactions: 0, interactionRate: 0, engagements: 0, engagementRate: 0,
  // Phone
  phoneImpressions: 0, phoneCalls: 0, phoneThroughRate: 0,
  // Reach
  averageFrequency: 0, uniqueUsers: 0,
  // Active View
  activeViewImpressions: 0, activeViewViewability: 0,
  activeViewMeasurability: 0, activeViewMeasurableImpressions: 0,
  // Video
  videoViews: 0, videoViewRate: 0, averageCpv: 0,
  videoQuartileP25: 0, videoQuartileP50: 0, videoQuartileP75: 0, videoQuartileP100: 0,
  videoWatchTimeMs: 0, avgVideoWatchTimeMs: 0,
};

function transformOverviewResults(
  results: Array<Record<string, unknown>>
): Record<string, unknown>[] {
  if (results.length === 0) {
    return [{ ...EMPTY_OVERVIEW }];
  }

  const metrics = (results[0] as Record<string, unknown>)?.metrics as
    | Record<string, unknown>
    | undefined;
  if (!metrics) {
    return [{ ...EMPTY_OVERVIEW }];
  }

  const clicks = parseMetric(metrics.clicks);
  const conversions = parseMetric(metrics.conversions);

  return [
    {
      _reportType: "overview",
      impressions: parseMetric(metrics.impressions),
      clicks,
      ctr: parseMetric(metrics.ctr),
      averageCpc: microsToCurrency(parseMetric(metrics.averageCpc)),
      cost: microsToCurrency(parseMetric(metrics.costMicros)),
      conversions,
      conversionRate: clicks > 0 ? conversions / clicks : 0,
      costPerConversion: microsToCurrency(
        parseMetric(metrics.costPerConversion)
      ),
      // Conversion value metrics (IMC-9)
      conversionsValue: parseMetric(metrics.conversionsValue),
      roas: parseMetric(metrics.conversionsValuePerCost),
      allRoas: parseMetric(metrics.allConversionsValuePerCost),
      allConversions: parseMetric(metrics.allConversions),
      allConversionsValue: parseMetric(metrics.allConversionsValue),
      viewThroughConversions: parseMetric(metrics.viewThroughConversions),
      crossDeviceConversions: parseMetric(metrics.crossDeviceConversions),
      valuePerConversion: parseMetric(metrics.valuePerConversion),
      costPerAllConversions: microsToCurrency(
        parseMetric(metrics.costPerAllConversions)
      ),
      conversionsByConvDate: parseMetric(metrics.conversionsByConversionDate),
      conversionsValueByConvDate: parseMetric(
        metrics.conversionsValueByConversionDate
      ),
      allConversionsByConvDate: parseMetric(
        metrics.allConversionsByConversionDate
      ),
      allConversionsValueByConvDate: parseMetric(
        metrics.allConversionsValueByConversionDate
      ),
      modelAttributedConversions: parseMetric(
        metrics.currentModelAttributedConversions
      ),
      modelAttributedConversionsValue: parseMetric(
        metrics.currentModelAttributedConversionsValue
      ),
      conversionsFromInteractionsRate: parseMetric(
        metrics.conversionsFromInteractionsRate
      ),
      // Delivery & engagement metrics
      averageCpm: microsToCurrency(parseMetric(metrics.averageCpm)),
      averageCpe: microsToCurrency(parseMetric(metrics.averageCpe)),
      averageCost: microsToCurrency(parseMetric(metrics.averageCost)),
      interactions: parseMetric(metrics.interactions),
      interactionRate: parseMetric(metrics.interactionRate),
      engagements: parseMetric(metrics.engagements),
      engagementRate: parseMetric(metrics.engagementRate),
      // Phone metrics
      phoneImpressions: parseMetric(metrics.phoneImpressions),
      phoneCalls: parseMetric(metrics.phoneCalls),
      phoneThroughRate: parseMetric(metrics.phoneThroughRate),
      // Reach
      averageFrequency: parseMetric(metrics.averageFrequency),
      uniqueUsers: parseMetric(metrics.uniqueUsers),
      // Active View
      activeViewImpressions: parseMetric(metrics.activeViewImpressions),
      activeViewViewability: parseMetric(metrics.activeViewViewability),
      activeViewMeasurability: parseMetric(metrics.activeViewMeasurability),
      activeViewMeasurableImpressions: parseMetric(metrics.activeViewMeasurableImpressions),
      // Video metrics
      videoViews: parseMetric(metrics.videoTrueviewViews),
      videoViewRate: parseMetric(metrics.videoTrueviewViewRate),
      averageCpv: microsToCurrency(parseMetric(metrics.trueviewAverageCpv)),
      videoQuartileP25: parseMetric(metrics.videoQuartileP25Rate),
      videoQuartileP50: parseMetric(metrics.videoQuartileP50Rate),
      videoQuartileP75: parseMetric(metrics.videoQuartileP75Rate),
      videoQuartileP100: parseMetric(metrics.videoQuartileP100Rate),
      videoWatchTimeMs: parseMetric(metrics.videoWatchTimeDurationMillis),
      avgVideoWatchTimeMs: parseMetric(metrics.averageVideoWatchTimeDurationMillis),
    },
  ];
}

/**
 * Parse an Impression Share value. IS metrics return null when data is insufficient.
 * Stored as raw fractions (0.0-1.0), consistent with all other percentage metrics (CTR, etc.).
 * Frontend handles ×100 for display.
 */
function parseImpressionShare(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n;
}

function transformCampaignResults(
  results: Array<Record<string, unknown>>
): Record<string, unknown>[] {
  return results.map((row) => {
    const campaign = row.campaign as Record<string, unknown> | undefined;
    const metrics = row.metrics as Record<string, unknown> | undefined;
    const clicks = parseMetric(metrics?.clicks);
    const conversions = parseMetric(metrics?.conversions);
    return {
      _reportType: "campaigns",
      campaignId: String(campaign?.id ?? ""),
      campaignName: String(campaign?.name ?? ""),
      campaignStatus: String(campaign?.status ?? ""),
      campaignType: String(campaign?.advertisingChannelType ?? ""),
      biddingStrategy: String(campaign?.biddingStrategyType ?? ""),
      // IMC-23: campaign labels dimension
      campaignLabels: Array.isArray(campaign?.labels) ? (campaign.labels as unknown[]).map(String).join(", ") : "",
      impressions: parseMetric(metrics?.impressions),
      clicks,
      ctr: parseMetric(metrics?.ctr),
      averageCpc: microsToCurrency(parseMetric(metrics?.averageCpc)),
      cost: microsToCurrency(parseMetric(metrics?.costMicros)),
      conversions,
      conversionRate: clicks > 0 ? conversions / clicks : 0,
      costPerConversion: microsToCurrency(
        parseMetric(metrics?.costPerConversion)
      ),
      // Conversion value metrics
      conversionsValue: parseMetric(metrics?.conversionsValue),
      roas: parseMetric(metrics?.conversionsValuePerCost),
      allConversions: parseMetric(metrics?.allConversions),
      allConversionsValue: parseMetric(metrics?.allConversionsValue),
      viewThroughConversions: parseMetric(metrics?.viewThroughConversions),
      crossDeviceConversions: parseMetric(metrics?.crossDeviceConversions),
      // Impression Share — Search
      searchImpressionShare: parseImpressionShare(metrics?.searchImpressionShare),
      searchBudgetLostIs: parseImpressionShare(metrics?.searchBudgetLostImpressionShare),
      searchRankLostIs: parseImpressionShare(metrics?.searchRankLostImpressionShare),
      searchAbsTopIs: parseImpressionShare(metrics?.searchAbsoluteTopImpressionShare),
      searchBudgetLostAbsTopIs: parseImpressionShare(metrics?.searchBudgetLostAbsoluteTopImpressionShare),
      searchRankLostAbsTopIs: parseImpressionShare(metrics?.searchRankLostAbsoluteTopImpressionShare),
      searchTopIs: parseImpressionShare(metrics?.searchTopImpressionShare),
      searchBudgetLostTopIs: parseImpressionShare(metrics?.searchBudgetLostTopImpressionShare),
      searchRankLostTopIs: parseImpressionShare(metrics?.searchRankLostTopImpressionShare),
      absTopImpressionPct: parseImpressionShare(metrics?.absoluteTopImpressionPercentage),
      topImpressionPct: parseImpressionShare(metrics?.topImpressionPercentage),
      // Impression Share — Display
      displayImpressionShare: parseImpressionShare(metrics?.displayImpressionShare),
      displayRankLostIs: parseImpressionShare(metrics?.displayRankLostImpressionShare),
      displayBudgetLostIs: parseImpressionShare(metrics?.displayBudgetLostImpressionShare),
      // Video metrics
      videoViews: parseMetric(metrics?.videoTrueviewViews),
      videoViewRate: parseMetric(metrics?.videoTrueviewViewRate),
      averageCpv: microsToCurrency(parseMetric(metrics?.trueviewAverageCpv)),
      videoQuartileP25: parseMetric(metrics?.videoQuartileP25Rate),
      videoQuartileP50: parseMetric(metrics?.videoQuartileP50Rate),
      videoQuartileP75: parseMetric(metrics?.videoQuartileP75Rate),
      videoQuartileP100: parseMetric(metrics?.videoQuartileP100Rate),
      videoWatchTimeMs: parseMetric(metrics?.videoWatchTimeDurationMillis),
      avgVideoWatchTimeMs: parseMetric(metrics?.averageVideoWatchTimeDurationMillis),
      // Delivery & engagement
      averageCpm: microsToCurrency(parseMetric(metrics?.averageCpm)),
      averageCpe: microsToCurrency(parseMetric(metrics?.averageCpe)),
      averageCost: microsToCurrency(parseMetric(metrics?.averageCost)),
      interactions: parseMetric(metrics?.interactions),
      interactionRate: parseMetric(metrics?.interactionRate),
      engagements: parseMetric(metrics?.engagements),
      engagementRate: parseMetric(metrics?.engagementRate),
      // Phone
      phoneImpressions: parseMetric(metrics?.phoneImpressions),
      phoneCalls: parseMetric(metrics?.phoneCalls),
      phoneThroughRate: parseMetric(metrics?.phoneThroughRate),
      // Reach
      averageFrequency: parseMetric(metrics?.averageFrequency),
      uniqueUsers: parseMetric(metrics?.uniqueUsers),
      // Active View
      activeViewImpressions: parseMetric(metrics?.activeViewImpressions),
      activeViewViewability: parseMetric(metrics?.activeViewViewability),
      activeViewMeasurability: parseMetric(metrics?.activeViewMeasurability),
      activeViewMeasurableImpressions: parseMetric(metrics?.activeViewMeasurableImpressions),
    };
  });
}

function transformAdGroupResults(
  results: Array<Record<string, unknown>>
): Record<string, unknown>[] {
  return results.map((row) => {
    const campaign = row.campaign as Record<string, unknown> | undefined;
    const adGroup = row.adGroup as Record<string, unknown> | undefined;
    const metrics = row.metrics as Record<string, unknown> | undefined;
    const clicks = parseMetric(metrics?.clicks);
    const conversions = parseMetric(metrics?.conversions);
    return {
      _reportType: "adGroups",
      campaignName: String(campaign?.name ?? ""),
      adGroupId: String(adGroup?.id ?? ""),
      adGroupName: String(adGroup?.name ?? ""),
      adGroupStatus: String(adGroup?.status ?? ""),
      impressions: parseMetric(metrics?.impressions),
      clicks,
      ctr: parseMetric(metrics?.ctr),
      cost: microsToCurrency(parseMetric(metrics?.costMicros)),
      conversions,
      conversionRate: clicks > 0 ? conversions / clicks : 0,
      costPerConversion: microsToCurrency(
        parseMetric(metrics?.costPerConversion)
      ),
    };
  });
}

function transformKeywordResults(
  results: Array<Record<string, unknown>>
): Record<string, unknown>[] {
  return results.map((row) => {
    const campaign = row.campaign as Record<string, unknown> | undefined;
    const adGroup = row.adGroup as Record<string, unknown> | undefined;
    const criterion = row.adGroupCriterion as
      | Record<string, unknown>
      | undefined;
    const keyword = criterion?.keyword as Record<string, unknown> | undefined;
    const metrics = row.metrics as Record<string, unknown> | undefined;
    const clicks = parseMetric(metrics?.clicks);
    const conversions = parseMetric(metrics?.conversions);
    return {
      _reportType: "keywords",
      campaignName: String(campaign?.name ?? ""),
      adGroupName: String(adGroup?.name ?? ""),
      keywordText: String(keyword?.text ?? ""),
      matchType: String(keyword?.matchType ?? ""),
      impressions: parseMetric(metrics?.impressions),
      clicks,
      ctr: parseMetric(metrics?.ctr),
      cost: microsToCurrency(parseMetric(metrics?.costMicros)),
      conversions,
      conversionRate: clicks > 0 ? conversions / clicks : 0,
      // Quality Score metrics (IMC-9 Task 3, IMC-23 AC #3) — nullable since Google returns null when insufficient data
      qualityScore: parseNullableMetric(metrics?.historicalQualityScore),
      // QS sub-components converted from enum buckets to 1-10 scores (IMC-23 AC #3)
      historicalCreativeQualityScore: parseQsBucket(metrics?.historicalCreativeQualityScore),
      historicalLandingPageQualityScore: parseQsBucket(metrics?.historicalLandingPageQualityScore),
      historicalSearchPredictedCtr: parseQsBucket(metrics?.historicalSearchPredictedCtr),
    };
  });
}

function transformSearchTermResults(
  results: Array<Record<string, unknown>>
): Record<string, unknown>[] {
  return results.map((row) => {
    const searchTermView = row.searchTermView as Record<string, unknown> | undefined;
    const segments = row.segments as Record<string, unknown> | undefined;
    const metrics = row.metrics as Record<string, unknown> | undefined;
    const clicks = parseMetric(metrics?.clicks);
    const conversions = parseMetric(metrics?.conversions);
    return {
      _reportType: "searchTerms",
      searchTerm: String(searchTermView?.searchTerm ?? ""),
      searchTermMatchType: String(segments?.searchTermMatchType ?? ""),
      impressions: parseMetric(metrics?.impressions),
      clicks,
      ctr: parseMetric(metrics?.ctr),
      cost: microsToCurrency(parseMetric(metrics?.costMicros)),
      conversions,
      conversionRate: clicks > 0 ? conversions / clicks : 0,
    };
  });
}

function transformShoppingResults(
  results: Array<Record<string, unknown>>
): Record<string, unknown>[] {
  return results.map((row) => {
    const segments = row.segments as Record<string, unknown> | undefined;
    const metrics = row.metrics as Record<string, unknown> | undefined;
    const revenue = microsToCurrency(parseMetric(metrics?.revenueMicros));
    const grossProfit = microsToCurrency(parseMetric(metrics?.grossProfitMicros));
    const conversions = parseMetric(metrics?.conversions);
    const unitsSold = parseMetric(metrics?.unitsSold);
    return {
      _reportType: "shopping",
      productItemId: String(segments?.productItemId ?? ""),
      productTitle: String(segments?.productTitle ?? ""),
      productBrand: String(segments?.productBrand ?? ""),
      productCategoryL1: String(segments?.productCategoryLevel1 ?? ""),
      // IMC-23: Shopping product dimensions
      productTypeL1: String(segments?.productTypeL1 ?? ""),
      productChannel: String(segments?.productChannel ?? ""),
      productCustomAttribute0: String(segments?.productCustomAttribute0 ?? ""),
      impressions: parseMetric(metrics?.impressions),
      clicks: parseMetric(metrics?.clicks),
      cost: microsToCurrency(parseMetric(metrics?.costMicros)),
      conversions,
      revenue,
      unitsSold,
      grossProfit,
      averageOrderValue: microsToCurrency(
        parseMetric(metrics?.averageOrderValueMicros)
      ),
      cogs: microsToCurrency(
        parseMetric(metrics?.costOfGoodsSoldMicros)
      ),
      // IMC-23: Cross-sell metrics
      crossSellRevenue: microsToCurrency(parseMetric(metrics?.crossSellRevenueMicros)),
      crossSellUnits: parseMetric(metrics?.crossSellUnitsSold),
      // IMC-23: Lead metrics
      leadRevenue: microsToCurrency(parseMetric(metrics?.leadRevenueMicros)),
      leadUnits: parseMetric(metrics?.leadUnitsSold),
      // IMC-23: Derived metrics (AC #4)
      grossProfitMargin: revenue > 0 ? Math.round(grossProfit / revenue * 10000) / 100 : 0,
      avgCartSize: conversions > 0 ? Math.round(unitsSold / conversions * 100) / 100 : 0,
    };
  });
}

function transformAgeRangeResults(
  results: Array<Record<string, unknown>>
): Record<string, unknown>[] {
  return results.map((row) => {
    const criterion = row.adGroupCriterion as Record<string, unknown> | undefined;
    const ageRange = criterion?.ageRange as Record<string, unknown> | undefined;
    const metrics = row.metrics as Record<string, unknown> | undefined;
    const clicks = parseMetric(metrics?.clicks);
    const conversions = parseMetric(metrics?.conversions);
    return {
      _reportType: "demographics",
      demographicType: "age",
      ageRange: String(ageRange?.type ?? ""),
      impressions: parseMetric(metrics?.impressions),
      clicks,
      ctr: parseMetric(metrics?.ctr),
      cost: microsToCurrency(parseMetric(metrics?.costMicros)),
      conversions,
      conversionRate: clicks > 0 ? conversions / clicks : 0,
    };
  });
}

function transformGenderResults(
  results: Array<Record<string, unknown>>
): Record<string, unknown>[] {
  return results.map((row) => {
    const criterion = row.adGroupCriterion as Record<string, unknown> | undefined;
    const gender = criterion?.gender as Record<string, unknown> | undefined;
    const metrics = row.metrics as Record<string, unknown> | undefined;
    const clicks = parseMetric(metrics?.clicks);
    const conversions = parseMetric(metrics?.conversions);
    return {
      _reportType: "demographics",
      demographicType: "gender",
      gender: String(gender?.type ?? ""),
      impressions: parseMetric(metrics?.impressions),
      clicks,
      ctr: parseMetric(metrics?.ctr),
      cost: microsToCurrency(parseMetric(metrics?.costMicros)),
      conversions,
      conversionRate: clicks > 0 ? conversions / clicks : 0,
    };
  });
}

// IMC-23: Ad-level transform
function transformAdResults(
  results: Array<Record<string, unknown>>
): Record<string, unknown>[] {
  return results.map((row) => {
    const campaign = row.campaign as Record<string, unknown> | undefined;
    const adGroup = row.adGroup as Record<string, unknown> | undefined;
    const adGroupAd = row.adGroupAd as Record<string, unknown> | undefined;
    const ad = adGroupAd?.ad as Record<string, unknown> | undefined;
    const metrics = row.metrics as Record<string, unknown> | undefined;
    const clicks = parseMetric(metrics?.clicks);
    const conversions = parseMetric(metrics?.conversions);
    return {
      _reportType: "ads",
      campaignName: String(campaign?.name ?? ""),
      adGroupName: String(adGroup?.name ?? ""),
      adId: String(ad?.id ?? ""),
      adType: String(ad?.type ?? ""),
      adStrength: String(adGroupAd?.adStrength ?? ""),
      impressions: parseMetric(metrics?.impressions),
      clicks,
      ctr: parseMetric(metrics?.ctr),
      cost: microsToCurrency(parseMetric(metrics?.costMicros)),
      conversions,
      conversionRate: clicks > 0 ? conversions / clicks : 0,
    };
  });
}

// IMC-23: Conversion detail transform
function transformConversionDetailResults(
  results: Array<Record<string, unknown>>
): Record<string, unknown>[] {
  return results.map((row) => {
    const segments = row.segments as Record<string, unknown> | undefined;
    const metrics = row.metrics as Record<string, unknown> | undefined;
    return {
      _reportType: "conversionDetail",
      conversionActionName: String(segments?.conversionActionName ?? ""),
      conversionActionCategory: String(segments?.conversionActionCategory ?? ""),
      conversionLagBucket: String(segments?.conversionLagBucket ?? ""),
      conversions: parseMetric(metrics?.conversions),
      conversionsValue: parseMetric(metrics?.conversionsValue),
      allConversions: parseMetric(metrics?.allConversions),
    };
  });
}

// ============================================================================
// Available Metrics Definition
// ============================================================================

const GOOGLE_ADS_METRICS: MetricDefinition[] = [
  // Core Traffic
  { key: "impressions", label: "Impressions", type: "number", category: "ads", dataType: "Integer", aggregationKind: "additive" },
  { key: "clicks", label: "Clicks", type: "number", category: "ads", dataType: "Integer", aggregationKind: "additive" },
  { key: "ctr", label: "CTR", type: "percentage", category: "ads", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "clicks", denominator: "impressions" } },
  { key: "cpc", label: "Avg. CPC", type: "currency", category: "ads", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "clicks" } },
  { key: "cost", label: "Cost", type: "currency", category: "ads", dataType: "Currency", aggregationKind: "additive" },
  { key: "average_cpm", label: "Avg. CPM", type: "currency", category: "ads", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "impressions" } },
  { key: "average_cpe", label: "Avg. CPE", type: "currency", category: "ads", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "engagements" } },
  { key: "average_cost", label: "Avg. Cost", type: "currency", category: "ads", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "interactions" } },
  { key: "interactions", label: "Interactions", type: "number", category: "ads", dataType: "Integer", aggregationKind: "additive" },
  { key: "interaction_rate", label: "Interaction Rate", type: "percentage", category: "ads", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "interactions", denominator: "impressions" } },
  { key: "engagements", label: "Engagements", type: "number", category: "ads", dataType: "Integer", aggregationKind: "additive" },
  { key: "engagement_rate", label: "Engagement Rate", type: "percentage", category: "ads", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "engagements", denominator: "impressions" } },
  { key: "active_view_impressions", label: "Active View Impressions", type: "number", category: "ads", dataType: "Integer", aggregationKind: "additive" },
  { key: "active_view_viewability", label: "Viewable Rate", type: "percentage", category: "ads", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "active_view_impressions", denominator: "active_view_measurable_impressions" } },
  { key: "active_view_measurability", label: "Active View Measurability", type: "percentage", category: "ads", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "active_view_measurable_impressions", denominator: "impressions" } },
  { key: "active_view_measurable_impressions", label: "Active View Measurable", type: "number", category: "ads", dataType: "Integer", aggregationKind: "additive" },
  // Conversions
  { key: "conversions", label: "Conversions", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "conversion_rate", label: "Conversion Rate", type: "percentage", category: "conversions", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "conversions", denominator: "interactions" } },
  { key: "cost_per_conversion", label: "Cost per Conversion", type: "currency", category: "conversions", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "conversions" } },
  { key: "conversions_value", label: "Conv. Value", type: "currency", category: "conversions", dataType: "Currency", aggregationKind: "additive" },
  { key: "roas", label: "ROAS", type: "number", category: "conversions", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "conversions_value", denominator: "cost" } },
  { key: "all_roas", label: "All ROAS", type: "number", category: "conversions", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "all_conversions_value", denominator: "cost" } },
  { key: "all_conversions", label: "All Conversions", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "all_conversions_value", label: "All Conv. Value", type: "currency", category: "conversions", dataType: "Currency", aggregationKind: "additive" },
  { key: "view_through_conversions", label: "View-Through Conv.", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "cross_device_conversions", label: "Cross-Device Conv.", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "value_per_conversion", label: "Value per Conv.", type: "currency", category: "conversions", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "conversions_value", denominator: "conversions" } },
  { key: "cost_per_all_conversions", label: "Cost per All Conv.", type: "currency", category: "conversions", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "all_conversions" } },
  { key: "conversions_by_conv_date", label: "Conv. (by conv. date)", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "conversions_value_by_conv_date", label: "Conv. Value (by conv. date)", type: "currency", category: "conversions", dataType: "Currency", aggregationKind: "additive" },
  { key: "all_conversions_by_conv_date", label: "All Conv. (by conv. date)", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "all_conversions_value_by_conv_date", label: "All Conv. Value (by conv. date)", type: "currency", category: "conversions", dataType: "Currency", aggregationKind: "additive" },
  { key: "model_attributed_conversions", label: "Model-Attributed Conv.", type: "number", category: "conversions", dataType: "Integer", aggregationKind: "additive" },
  { key: "model_attributed_conversions_value", label: "Model-Attributed Conv. Value", type: "currency", category: "conversions", dataType: "Currency", aggregationKind: "additive" },
  { key: "conversions_from_interactions_rate", label: "Conv. from Interactions Rate", type: "percentage", category: "conversions", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "conversions", denominator: "interactions" } },
  // Impression Share — Search
  { key: "search_impression_share", label: "Search IS", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "search_budget_lost_is", label: "Search Lost IS (Budget)", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "search_rank_lost_is", label: "Search Lost IS (Rank)", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "search_abs_top_is", label: "Search Abs. Top IS", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "search_budget_lost_abs_top_is", label: "Abs. Top Lost IS (Budget)", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "search_rank_lost_abs_top_is", label: "Abs. Top Lost IS (Rank)", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "search_top_is", label: "Search Top IS", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "search_budget_lost_top_is", label: "Top Lost IS (Budget)", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "search_rank_lost_top_is", label: "Top Lost IS (Rank)", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "abs_top_impression_pct", label: "Impr. (Abs. Top) %", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" } },
  { key: "top_impression_pct", label: "Impr. (Top) %", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" } },
  // Impression Share — Display
  { key: "display_impression_share", label: "Display IS", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "display_rank_lost_is", label: "Display Lost IS (Rank)", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  { key: "display_budget_lost_is", label: "Display Lost IS (Budget)", type: "percentage", category: "impression_share", dataType: "Percentage", aggregationKind: "ratio" },
  // Quality Score (keyword_view) — QS 1-10 + sub-components converted from enum buckets to 1-10 scores (IMC-23)
  { key: "quality_score", label: "Quality Score", type: "number", category: "quality_score", dataType: "Integer", aggregationKind: "ratio" },
  { key: "historical_creative_quality_score", label: "Ad Relevance (QS)", type: "number", category: "quality_score", dataType: "Integer", aggregationKind: "ratio" },
  { key: "historical_landing_page_quality_score", label: "Landing Page Experience (QS)", type: "number", category: "quality_score", dataType: "Integer", aggregationKind: "ratio" },
  { key: "historical_search_predicted_ctr", label: "Expected CTR (QS)", type: "number", category: "quality_score", dataType: "Integer", aggregationKind: "ratio" },
  // Video
  { key: "video_views", label: "Video Views", type: "number", category: "video", dataType: "Integer", aggregationKind: "additive" },
  { key: "video_view_rate", label: "View Rate", type: "percentage", category: "video", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "video_views", denominator: "impressions" } },
  { key: "average_cpv", label: "Avg. CPV", type: "currency", category: "video", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "cost", denominator: "video_views" } },
  { key: "video_quartile_p25", label: "Video 25%", type: "percentage", category: "video", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" } },
  { key: "video_quartile_p50", label: "Video 50%", type: "percentage", category: "video", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" } },
  { key: "video_quartile_p75", label: "Video 75%", type: "percentage", category: "video", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" } },
  { key: "video_quartile_p100", label: "Video 100%", type: "percentage", category: "video", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { denominator: "impressions" } },
  { key: "video_watch_time_ms", label: "Total Watch Time", type: "number", category: "video", dataType: "Integer", aggregationKind: "additive" },
  { key: "avg_video_watch_time_ms", label: "Avg. Watch Time", type: "number", category: "video", dataType: "Integer", aggregationKind: "ratio", ratioComponents: { numerator: "video_watch_time_ms", denominator: "video_views" } },
  // Shopping
  { key: "revenue", label: "Revenue", type: "currency", category: "shopping", dataType: "Currency", aggregationKind: "additive" },
  { key: "units_sold", label: "Units Sold", type: "number", category: "shopping", dataType: "Integer", aggregationKind: "additive" },
  { key: "gross_profit", label: "Gross Profit", type: "currency", category: "shopping", dataType: "Currency", aggregationKind: "additive" },
  { key: "average_order_value", label: "AOV", type: "currency", category: "shopping", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "revenue", denominator: "conversions" } },
  { key: "cogs", label: "COGS", type: "currency", category: "shopping", dataType: "Currency", aggregationKind: "additive" },
  // Shopping — IMC-23: derived + cross-sell + lead
  { key: "gross_profit_margin", label: "Gross Profit Margin", type: "percentage", category: "shopping", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "gross_profit", denominator: "revenue" } },
  { key: "avg_cart_size", label: "Avg. Cart Size", type: "number", category: "shopping", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "units_sold", denominator: "conversions" } },
  { key: "cross_sell_revenue", label: "Cross-Sell Revenue", type: "currency", category: "shopping", dataType: "Currency", aggregationKind: "additive" },
  { key: "cross_sell_units", label: "Cross-Sell Units", type: "number", category: "shopping", dataType: "Integer", aggregationKind: "additive" },
  { key: "lead_revenue", label: "Lead Revenue", type: "currency", category: "shopping", dataType: "Currency", aggregationKind: "additive" },
  { key: "lead_units", label: "Lead Units", type: "number", category: "shopping", dataType: "Integer", aggregationKind: "additive" },
  // Phone
  { key: "phone_impressions", label: "Phone Impressions", type: "number", category: "phone", dataType: "Integer", aggregationKind: "additive" },
  { key: "phone_calls", label: "Phone Calls", type: "number", category: "phone", dataType: "Integer", aggregationKind: "additive" },
  { key: "phone_through_rate", label: "Phone-Through Rate", type: "percentage", category: "phone", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "phone_calls", denominator: "phone_impressions" } },
  // Reach
  { key: "average_frequency", label: "Avg. Frequency", type: "number", category: "reach", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { numerator: "impressions", denominator: "unique_users" } },
  { key: "unique_users", label: "Reach (Unique Users)", type: "number", category: "reach", dataType: "Integer", aggregationKind: "additive" },
];

// ============================================================================
// Available Dimensions Definition (Story 5.34)
// ============================================================================

const GOOGLE_ADS_DIMENSIONS: DimensionDefinition[] = [
  // Time
  { key: "date", label: "Date", type: "Time", exampleValues: ["2026-03-01"] },
  { key: "segments.week", label: "Week", type: "Time", exampleValues: ["2026-W10"] },
  { key: "segments.month", label: "Month", type: "Time", exampleValues: ["2026-03"] },
  { key: "segments.day_of_week", label: "Day of Week", type: "Time", exampleValues: ["MONDAY", "FRIDAY"] },
  { key: "segments.hour", label: "Hour", type: "Time", exampleValues: ["9", "17"] },
  // IMC-23: Time dimensions
  { key: "segments.quarter", label: "Quarter", type: "Time", exampleValues: ["Q1", "Q2", "Q3", "Q4"] },
  { key: "segments.year", label: "Year", type: "Time", exampleValues: ["2025", "2026"] },
  // Campaign Structure
  { key: "campaign.name", label: "Campaign", type: "Structure", exampleValues: ["Brand Search", "Shopping - US"] },
  { key: "campaign.id", label: "Campaign ID", type: "Structure", exampleValues: ["123456789"] },
  { key: "campaign.advertising_channel_type", label: "Campaign Type", type: "Structure", exampleValues: ["SEARCH", "DISPLAY", "PERFORMANCE_MAX"] },
  { key: "campaign.bidding_strategy_type", label: "Bidding Strategy", type: "Structure", exampleValues: ["TARGET_CPA", "MAXIMIZE_CONVERSIONS"] },
  { key: "ad_group.name", label: "Ad Group", type: "Structure", exampleValues: ["Brand Terms", "Competitor Terms"] },
  { key: "ad_group.id", label: "Ad Group ID", type: "Structure", exampleValues: ["987654321"] },
  // IMC-23: Campaign dimensions
  { key: "campaign.status", label: "Campaign Status", type: "Structure", exampleValues: ["ENABLED", "PAUSED", "REMOVED"] },
  { key: "campaign.labels", label: "Campaign Labels", type: "Structure", exampleValues: ["customers/123/labels/Brand", "customers/123/labels/Q2"] },
  // IMC-23: Ad-level dimensions
  { key: "ad_group_ad.ad.id", label: "Ad ID", type: "Structure", exampleValues: ["11223344"] },
  { key: "ad_group_ad.ad.type", label: "Ad Type", type: "Structure", exampleValues: ["RESPONSIVE_SEARCH_AD", "EXPANDED_TEXT_AD"] },
  { key: "ad_group_ad.ad_strength", label: "Ad Strength", type: "Structure", exampleValues: ["EXCELLENT", "GOOD", "AVERAGE", "POOR"] },
  // Keyword & Search
  { key: "ad_group_criterion.keyword.text", label: "Keyword", type: "Search", exampleValues: ["marketing analytics", "seo tool"] },
  { key: "ad_group_criterion.keyword.match_type", label: "Match Type", type: "Search", exampleValues: ["EXACT", "PHRASE", "BROAD"] },
  { key: "search_term_view.search_term", label: "Search Term", type: "Search", exampleValues: ["best marketing tool", "analytics software"] },
  { key: "segments.search_term_match_type", label: "Search Match Type", type: "Search", exampleValues: ["EXACT", "PHRASE", "BROAD", "NEAR_EXACT"] },
  // Network
  { key: "segments.ad_network_type", label: "Network", type: "Tech", exampleValues: ["SEARCH", "CONTENT", "YOUTUBE_WATCH"] },
  { key: "segments.device", label: "Device", type: "Tech", exampleValues: ["DESKTOP", "MOBILE", "TABLET"] },
  // Geography
  { key: "geographic_view.country_criterion_id", label: "Country", type: "Geo", exampleValues: ["2840", "2826"] },
  { key: "segments.geo_target_region", label: "Region", type: "Geo", exampleValues: ["21167", "21171"] },
  { key: "segments.geo_target_city", label: "City", type: "Geo", exampleValues: ["1014221", "1014895"] },
  // IMC-23: Geographic detail dimensions
  { key: "segments.geo_target_metro", label: "Metro (DMA)", type: "Geo", exampleValues: ["200501", "200510"] },
  { key: "segments.geo_target_postal_code", label: "Postal Code", type: "Geo", exampleValues: ["1014221", "1023191"] },
  // Demographics
  { key: "ad_group_criterion.age_range.type", label: "Age Range", type: "Demo", exampleValues: ["AGE_RANGE_25_34", "AGE_RANGE_35_44"] },
  { key: "ad_group_criterion.gender.type", label: "Gender", type: "Demo", exampleValues: ["MALE", "FEMALE", "UNDETERMINED"] },
  // IMC-23: income range (already available in demographics queries)
  { key: "ad_group_criterion.income_range.type", label: "Income Range", type: "Demo", exampleValues: ["INCOME_RANGE_50_60", "INCOME_RANGE_60_70"] },
  // Shopping / Product
  { key: "segments.product_item_id", label: "Product ID (SKU)", type: "Product", exampleValues: ["SKU-12345"] },
  { key: "segments.product_title", label: "Product Title", type: "Product", exampleValues: ["Blue Widget", "Red Gadget"] },
  { key: "segments.product_brand", label: "Product Brand", type: "Product", exampleValues: ["BrandX", "BrandY"] },
  { key: "segments.product_category_level1", label: "Product Category", type: "Product", exampleValues: ["Electronics", "Clothing"] },
  // IMC-23: Shopping dimensions
  { key: "segments.product_type_l1", label: "Product Type", type: "Product", exampleValues: ["Apparel", "Electronics"] },
  { key: "segments.product_channel", label: "Product Channel", type: "Product", exampleValues: ["ONLINE", "LOCAL"] },
  { key: "segments.product_custom_attribute0", label: "Custom Attribute 0", type: "Product", exampleValues: ["bestseller", "clearance"] },
  // PMax (v23+)
  { key: "segments.ad_using_product_data", label: "Using Product Data", type: "Structure", exampleValues: ["true", "false"] },
  { key: "segments.ad_using_video", label: "Using Video", type: "Structure", exampleValues: ["true", "false"] },
  // IMC-23: Conversion detail dimensions
  { key: "segments.conversion_action_name", label: "Conversion Action", type: "Conversion", exampleValues: ["Purchase", "Lead Form Submit"] },
  { key: "segments.conversion_action_category", label: "Conversion Category", type: "Conversion", exampleValues: ["PURCHASE", "SUBMIT_LEAD_FORM"] },
  { key: "segments.conversion_lag_bucket", label: "Conversion Lag", type: "Conversion", exampleValues: ["LESS_THAN_ONE_DAY", "ONE_TO_TWO_DAYS"] },
  // IMC-23: Account metadata dimensions
  { key: "customer.descriptive_name", label: "Account Name", type: "Structure", exampleValues: ["My Business Account"] },
  { key: "customer.currency_code", label: "Currency", type: "Structure", exampleValues: ["USD", "EUR", "GBP"] },
];

// ============================================================================
// Google Ads Adapter (IntegrationAdapter implementation)
// ============================================================================

export const googleAdsAdapter: IntegrationAdapter = {
  platform: PLATFORM,

  rateLimits: {
    requestsPerMinute: 60,
    requestsPerDay: 15_000,
  },

  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const accountId = ctx.accountId;

    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message:
          "No Google Ads account selected. Please select an account first.",
        retryable: false,
      });
    }

    // Google Ads customer IDs are numeric (up to 10 digits)
    if (!/^\d{1,10}$/.test(accountId)) {
      return adapterErr({
        code: "invalid_response",
        message: "Invalid Google Ads account ID format",
        retryable: false,
      });
    }

    // Developer token is required for all Google Ads API calls
    const developerToken = (
      ctx.env as unknown as Record<string, unknown>
    ).GOOGLE_ADS_DEVELOPER_TOKEN as string | undefined;
    if (!developerToken) {
      return adapterErr({
        code: "permission_denied",
        message: "Google Ads developer token not configured",
        retryable: false,
      });
    }

    // Convert Date objects to YYYY-MM-DD strings
    const startDateStr = dateRange.start.toISOString().split("T")[0];
    const endDateStr = dateRange.end.toISOString().split("T")[0];

    if (!startDateStr || !endDateStr) {
      return adapterErr({
        code: "invalid_response",
        message: "Invalid date range: could not format dates",
        retryable: false,
      });
    }

    // MCC login-customer-id support (passed from route via env)
    const loginCustId = (
      ctx.env as unknown as Record<string, unknown>
    ).LOGIN_CUSTOMER_ID as string | undefined;

    const headers = buildGoogleAdsHeaders(
      credentials.accessToken,
      developerToken,
      loginCustId
    );

    // 10 parallel GAQL queries via Promise.all — errors checked after all resolve
    // Each query = 1 API operation. 10 queries well within 15K ops/day Basic tier.
    const queries = [
      { name: "overview", query: buildOverviewQuery(startDateStr, endDateStr) },
      {
        name: "campaigns",
        query: buildCampaignsQuery(startDateStr, endDateStr),
      },
      {
        name: "adGroups",
        query: buildAdGroupsQuery(startDateStr, endDateStr),
      },
      {
        name: "keywords",
        query: buildKeywordsQuery(startDateStr, endDateStr),
      },
      {
        name: "searchTerms",
        query: buildSearchTermsQuery(startDateStr, endDateStr),
      },
      {
        name: "shopping",
        query: buildShoppingQuery(startDateStr, endDateStr),
      },
      {
        name: "ageRange",
        query: buildAgeRangeQuery(startDateStr, endDateStr),
      },
      {
        name: "gender",
        query: buildGenderQuery(startDateStr, endDateStr),
      },
      // IMC-23: Ad-level query
      {
        name: "ads",
        query: buildAdsQuery(startDateStr, endDateStr),
      },
      // IMC-23: Conversion detail query
      {
        name: "conversionDetail",
        query: buildConversionDetailQuery(startDateStr, endDateStr),
      },
    ];

    // Execute all GAQL queries in parallel to avoid Worker timeout (ADR perf fix)
    const rawResults = await Promise.all(
      queries.map((q) =>
        executeGaqlQuery(accountId, q.query, q.name, headers, QUERY_TIMEOUT_MS)
      )
    );

    // Check for errors (all-or-nothing)
    for (const result of rawResults) {
      if (!result.success) {
        return adapterErr(result.error);
      }
    }

    // Transform results based on query index (order preserved by Promise.all)
    const transformByIndex: Record<number, (data: Record<string, unknown>[]) => Record<string, unknown>[]> = {
      [REPORT_INDEX.OVERVIEW]: transformOverviewResults,
      [REPORT_INDEX.CAMPAIGNS]: transformCampaignResults,
      [REPORT_INDEX.AD_GROUPS]: transformAdGroupResults,
      [REPORT_INDEX.KEYWORDS]: transformKeywordResults,
      [REPORT_INDEX.SEARCH_TERMS]: transformSearchTermResults,
      [REPORT_INDEX.SHOPPING]: transformShoppingResults,
      [REPORT_INDEX.AGE_RANGE]: transformAgeRangeResults,
      [REPORT_INDEX.GENDER]: transformGenderResults,
      [REPORT_INDEX.ADS]: transformAdResults,
      [REPORT_INDEX.CONVERSION_DETAIL]: transformConversionDetailResults,
    };

    const allRecords: Record<string, unknown>[] = [];
    for (let i = 0; i < rawResults.length; i++) {
      const result = rawResults[i]!;
      // result.success is guaranteed true after the error check above
      const transform = transformByIndex[i];
      const records = transform ? transform((result as { success: true; data: Record<string, unknown>[] }).data) : [];
      allRecords.push(...records);
    }

    console.log(
      JSON.stringify({
        event: "google_ads_fetch_complete",
        integrationId: ctx.integrationId,
        tenantId: ctx.tenantId,
        queriesExecuted: queries.length,
        recordCount: allRecords.length,
        timestamp: new Date().toISOString(),
      })
    );

    return adapterOk({
      platform: PLATFORM,
      accountId,
      dateRange: { start: dateRange.start, end: dateRange.end },
      records: allRecords,
      fetchedAt: new Date(),
      recordCount: allRecords.length,
    });
  },

  async validateConnection(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const accountId = ctx.accountId;

    if (!accountId) {
      return adapterErr({
        code: "account_not_found",
        message:
          "No Google Ads account selected for connection validation.",
        retryable: false,
      });
    }

    if (!/^\d{1,10}$/.test(accountId)) {
      return adapterErr({
        code: "invalid_response",
        message: "Invalid Google Ads account ID format",
        retryable: false,
      });
    }

    const developerToken = (
      ctx.env as unknown as Record<string, unknown>
    ).GOOGLE_ADS_DEVELOPER_TOKEN as string | undefined;
    if (!developerToken) {
      return adapterErr({
        code: "permission_denied",
        message: "Google Ads developer token not configured",
        retryable: false,
      });
    }

    const loginCustId = (
      ctx.env as unknown as Record<string, unknown>
    ).LOGIN_CUSTOMER_ID as string | undefined;

    const headers = buildGoogleAdsHeaders(
      credentials.accessToken,
      developerToken,
      loginCustId
    );

    // Lightweight GAQL query with 5-second timeout
    const query = "SELECT customer.id FROM customer LIMIT 1";
    const url = `${GOOGLE_ADS_API_BASE}/${API_VERSIONS.googleAds}/customers/${accountId}/googleAds:searchStream`;

    const result = await safeFetch(
      url,
      {
        method: "POST",
        headers,
        body: JSON.stringify({ query }),
      },
      VALIDATE_TIMEOUT_MS
    );

    if (!result.success) {
      return adapterErr(result.error);
    }

    // Consume response body exactly once
    try {
      await result.data.json();
    } catch {
      // Body consumption failed — connection is still valid if status was OK
    }

    return adapterOk(true);
  },

  /**
   * Catalog discovery (Story 31-16 — multi-tenant FULL catalog).
   *
   * Pipeline (per the 31-4 catalog contract in `adapter.ts`):
   * 1. SEED (sacrosanct membership): `listAccessibleCustomers` via
   *    `discoverGoogleAdsAccounts()` — the sole source for direct rows.
   * 2. ENRICH (best-effort): per-seed GAQL `customer` detail query adds
   *    real name, `isManager`, `currencyCode`, `timeZone`.
   * 3. EXPAND (best-effort per manager): one `customer_client` query per
   *    `isManager: true` seed (with `login-customer-id` header) returns the
   *    ENTIRE subtree in one searchStream call — no recursion, no paging.
   *    A failed expansion logs + skips THAT manager only; seed membership
   *    and other managers' expansions survive.
   * 4. DEDUPE (defensive, ADR-0004 §Anti-Patterns 4): Map by customer id —
   *    direct (seed) entries win over expansion entries; a dropped expansion
   *    duplicate merges its `managerId` into the survivor's metadata.
   *
   * Known degrade: when a manager's detail query fails, the enrichment
   * fallback hardcodes `isManager: false`, so that manager gets NO expansion
   * this run. Acceptable — discovery is re-runnable (31-24 cron) and the
   * alternative (speculative expansion of every account) is an N× subrequest
   * fan-out.
   */
  async discoverAccounts(
    ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    const developerToken = (
      ctx.env as unknown as Record<string, unknown>
    ).GOOGLE_ADS_DEVELOPER_TOKEN as string | undefined;

    if (!developerToken) {
      return adapterErr({
        code: "permission_denied",
        message: "Google Ads developer token not configured",
        retryable: false,
      });
    }

    // Delegate to existing discoverGoogleAdsAccounts()
    const result = await discoverGoogleAdsAccounts(
      credentials.accessToken,
      developerToken
    );

    if (!result.success) {
      return adapterErr(result.error);
    }

    // Post-process: fetch customer details to get real names and manager status.
    // Use parallel batched enrichment to avoid N+1 sequential calls that exceed
    // the 30s Cloudflare Worker wall-clock limit for agencies with many accounts.
    const headers = buildGoogleAdsHeaders(
      credentials.accessToken,
      developerToken
    );

    const detailQuery =
      "SELECT customer.id, customer.descriptive_name, customer.manager, customer.currency_code, customer.time_zone FROM customer";

    async function enrichAccount(account: AccountInfo): Promise<AccountInfo> {
      const detailResult = await executeGaqlQuery(
        account.id,
        detailQuery,
        "customerDetails",
        headers,
        VALIDATE_TIMEOUT_MS
      );

      if (detailResult.success && detailResult.data.length > 0) {
        const customer = detailResult.data[0]?.customer as
          | Record<string, unknown>
          | undefined;
        const descriptiveName = customer?.descriptiveName as string | undefined;
        const isManager = customer?.manager === true;
        const currencyCode = customer?.currencyCode as string | undefined;
        const timeZone = customer?.timeZone as string | undefined;

        const accountName = descriptiveName
          ? isManager
            ? `${descriptiveName} (Manager)`
            : descriptiveName
          : account.name;

        return {
          ...account,
          name: accountName,
          metadata: {
            ...account.metadata,
            accountName: descriptiveName ?? `Account ${account.id}`,
            isManager,
            // Story 31-16 AC #2 — stub metadata triple for direct rows
            // (no managerId: direct rows are roots, not subtree members).
            ...(currencyCode !== undefined ? { currencyCode } : {}),
            ...(timeZone !== undefined ? { timeZone } : {}),
          },
        };
      }

      // Fallback: keep original name if detail query fails
      return {
        ...account,
        metadata: {
          ...account.metadata,
          accountName: account.name,
          isManager: false,
        },
      };
    }

    // Process in batches of MAX_PARALLEL_ENRICHMENTS to stay within Worker subrequest limits
    const enrichedAccounts: AccountInfo[] = [];
    for (let i = 0; i < result.data.length; i += MAX_PARALLEL_ENRICHMENTS) {
      const batch = result.data.slice(i, i + MAX_PARALLEL_ENRICHMENTS);
      const settled = await Promise.allSettled(batch.map(enrichAccount));
      for (let j = 0; j < settled.length; j++) {
        const s = settled[j]!;
        if (s.status === "fulfilled") {
          enrichedAccounts.push(s.value);
        } else {
          // On unexpected rejection, fall back to basic account info
          const original = batch[j]!;
          enrichedAccounts.push({
            ...original,
            metadata: {
              ...original.metadata,
              accountName: original.name,
              isManager: false,
            },
          });
        }
      }
    }

    // ------------------------------------------------------------------
    // Manager-account expansion (Story 31-16 AC #1/#2/#5)
    // ------------------------------------------------------------------
    const managerAccounts = enrichedAccounts.filter(
      (a) => a.metadata?.isManager === true
    );

    /**
     * Expand one manager's subtree via `customer_client`.
     * Returns the mapped level>=1 rows, or null when the query failed
     * (HTTP error / timeout / parse failure) — the caller logs + skips
     * THAT manager only (AC #5; mirrors the enrichment-failure philosophy).
     */
    async function expandManager(
      manager: AccountInfo
    ): Promise<AccountInfo[] | null> {
      const expansionResult = await executeGaqlQuery(
        manager.id,
        CUSTOMER_CLIENT_QUERY,
        "customerClients",
        // login-customer-id = the manager being expanded (Google's
        // recommended pattern; some permission setups require it).
        buildGoogleAdsHeaders(
          credentials.accessToken,
          developerToken!,
          manager.id
        ),
        VALIDATE_TIMEOUT_MS
      );

      if (!expansionResult.success) {
        return null;
      }

      const rows: AccountInfo[] = [];
      for (const row of expansionResult.data) {
        const client = row.customerClient as
          | Record<string, unknown>
          | undefined;
        if (!client) continue;

        const id =
          client.id !== undefined && client.id !== null
            ? String(client.id)
            : "";
        if (!id) continue;

        // Level 0 IS the queried manager — already in the catalog from the
        // seed list; skipping prevents a self-duplicate with
        // expansion-shaped (poorer) metadata (AC #1).
        //
        // REST returns int64 fields as strings AND proto3 JSON omits
        // default-valued fields, so the level-0 row can arrive with `level`
        // ABSENT (0 is the proto default) — `Number(undefined)` is NaN, not
        // 0. Only finite level >= 1 rows are catalog candidates; a
        // belt-and-braces self-id guard ensures the manager never re-enters
        // as its own expansion row (the dedupe would otherwise merge
        // `managerId: <self>` into the manager's own metadata).
        // (Code-review fix, Story 31-16.)
        const level = Number(client.level);
        if (!Number.isFinite(level) || level < 1 || id === manager.id) {
          continue;
        }

        const descriptiveName = client.descriptiveName as string | undefined;
        const isManager = client.manager === true;
        const currencyCode = client.currencyCode as string | undefined;
        const timeZone = client.timeZone as string | undefined;
        const accountName = descriptiveName ?? `Account ${id}`;

        rows.push({
          // Bare numeric customer id — frozen format (AC #8), must keep
          // matching the /^\d{1,10}$/ accountId validation and 31-3 rows.
          id,
          name: isManager ? `${accountName} (Manager)` : accountName,
          platform: PLATFORM,
          metadata: {
            accountName,
            isManager,
            // For level >= 2 this is the queried ROOT manager, not the
            // immediate parent (true parents need customer_client_link
            // recursion — explicitly out of scope; `level` keeps the
            // depth signal). AC #2.
            managerId: manager.id,
            level,
            ...(currencyCode !== undefined ? { currencyCode } : {}),
            ...(timeZone !== undefined ? { timeZone } : {}),
          },
          scopes: googleAdsCatalogScopes(),
        });
      }
      return rows;
    }

    // Managers are few, but stay within Worker subrequest discipline by
    // reusing the MAX_PARALLEL_ENRICHMENTS batching pattern (Task 3.3).
    const expansionAccounts: AccountInfo[] = [];
    const failedManagerIds: string[] = [];
    for (let i = 0; i < managerAccounts.length; i += MAX_PARALLEL_ENRICHMENTS) {
      const batch = managerAccounts.slice(i, i + MAX_PARALLEL_ENRICHMENTS);
      const settled = await Promise.allSettled(batch.map(expandManager));
      for (let j = 0; j < settled.length; j++) {
        const s = settled[j]!;
        if (s.status === "fulfilled" && s.value !== null) {
          expansionAccounts.push(...s.value);
        } else {
          failedManagerIds.push(batch[j]!.id);
        }
      }
    }

    if (failedManagerIds.length > 0) {
      // Structured partial-discovery warn (Wave A pattern) — membership seed
      // and other managers' expansions are unaffected; correlatable via
      // tenantId/integrationId when the 31-24 cron fans out across grants.
      console.warn(
        JSON.stringify({
          event: "google_ads_discovery_expansion_partial",
          tenantId: ctx.tenantId,
          integrationId: ctx.integrationId,
          failedManagerIds,
          message:
            "customer_client expansion failed for some Google Ads manager accounts; their client subtrees are omitted this run (seed catalog and other managers' expansions unaffected)",
        })
      );
    }

    // ------------------------------------------------------------------
    // Defensive dedupe by customer id (Story 31-16 AC #4, ADR-0004 §AP-4)
    // ------------------------------------------------------------------
    // Direct (seed) entries win over expansion entries — they carry the
    // richer resourceName metadata. A dropped expansion duplicate merges its
    // managerId into the survivor (hierarchy signal preserved). Between two
    // expansion duplicates, first occurrence wins. The DB UNIQUE index is
    // the backstop, not the first line.
    const catalog = new Map<string, AccountInfo>();
    for (const account of enrichedAccounts) {
      if (!catalog.has(account.id)) {
        catalog.set(account.id, account);
      }
    }
    for (const account of expansionAccounts) {
      const existing = catalog.get(account.id);
      if (existing) {
        const managerId = account.metadata?.managerId;
        if (
          managerId !== undefined &&
          existing.metadata?.managerId === undefined
        ) {
          catalog.set(account.id, {
            ...existing,
            metadata: { ...existing.metadata, managerId },
          });
        }
      } else {
        catalog.set(account.id, account);
      }
    }

    return adapterOk([...catalog.values()]);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return GOOGLE_ADS_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return GOOGLE_ADS_DIMENSIONS;
  },
};
